import React from 'react';
import { useSelector } from 'react-redux';
import core from '@gin-betting/core';
import { parse } from 'query-string';

import { DEFAULT_STREAM_URL } from 'app-constants';
import { State } from 'types';
import { PlayerType } from '../../constants';
import { InitPlayerFn } from '../../types';
import PlayerAdapter from '../PlayerAdapter';
import TwitchEmbedVideo from '../TwitchEmbedVideo';

const {
    viewport: {
        selectors: { makeViewportSelector },
    },
} = core;

interface EmbedParams {
    channel?: string;
}

interface Props {
    playerNodeId: string;
    streamUrl: string;
    autoplay?: boolean;
    changePlayStatus: (isVideoPlaying: boolean) => void;
    initPlayer?: InitPlayerFn;
}

// NOTE: base url can be https://player.twitch.tv/... or https://www.twitch.tv/...
const TWITCH_PART_OF_BASE_URL = 'twitch.tv/' as const;

const TwitchWrapper: React.FC<Props> = props => {
    const { streamUrl, autoplay, playerNodeId } = props;

    const onPlay = () => {
        // may be needed for debugging or fixes in theater mode behaviour
        props.changePlayStatus(true);
    };

    const onPause = () => {
        // may be needed for debugging or fixes in theater mode behaviour
        props.changePlayStatus(false);
    };

    const initPlayer = (player: TwitchPlayer | null) => {
        if (props.initPlayer) {
            props.initPlayer(
                new PlayerAdapter({
                    player,
                    type: PlayerType.TWITCH,
                })
            );
        }
    };

    let embedParams: EmbedParams = {};

    if (streamUrl !== DEFAULT_STREAM_URL) {
        const [path, paramsStr] = streamUrl.split('?');

        if (paramsStr) {
            embedParams = parse(paramsStr);
        }

        if (!embedParams.channel && path) {
            // eslint-disable-next-line
            const [_, channel] = path.split(TWITCH_PART_OF_BASE_URL);

            embedParams.channel = channel;
        }
    }

    const { medium, small } = useSelector((state: State) =>
        makeViewportSelector('lessThanOrEqual')(state, {})
    );

    const isSmallMedium = medium && !small;

    return (
        <TwitchEmbedVideo
            playerNodeId={playerNodeId}
            channel={embedParams.channel}
            onPlay={onPlay}
            onPause={onPause}
            initPlayer={initPlayer}
            autoplay={autoplay}
            isSmallMedium={isSmallMedium}
        />
    );
};

export default TwitchWrapper;
